import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePrestadores } from '@/hooks/usePrestadores';
import type { FrequenciaRecorrencia } from '@/types/recorrencia';

export type RecorrenciaStatusFiltro = 'todos' | 'ativa' | 'pausada';

export interface RecorrenciasFiltrosState {
  status: RecorrenciaStatusFiltro;
  frequencia?: FrequenciaRecorrencia;
  prestadorId?: string; // '__none__' = sem prestador fixo
}

// eslint-disable-next-line react-refresh/only-export-components
export const RECORRENCIAS_FILTROS_INICIAL: RecorrenciasFiltrosState = {
  status: 'todos',
};

interface Props {
  filtros: RecorrenciasFiltrosState;
  onChange: (next: RecorrenciasFiltrosState) => void;
}

export function RecorrenciasFiltros({ filtros, onChange }: Props) {
  const { t } = useTranslation();
  const { data: prestadores } = usePrestadores();

  const temFiltrosAtivos =
    filtros.status !== 'todos' ||
    filtros.frequencia !== undefined ||
    filtros.prestadorId !== undefined;

  const limpar = () => onChange(RECORRENCIAS_FILTROS_INICIAL);

  const statusOpts: { value: RecorrenciaStatusFiltro; label: string }[] = [
    { value: 'todos', label: t('common.all', 'Todos') },
    { value: 'ativa', label: t('recurrences.status.active') },
    { value: 'pausada', label: t('recurrences.status.paused') },
  ];

  const freqOpts: { value: FrequenciaRecorrencia | 'todas'; label: string }[] = [
    { value: 'todas', label: t('common.all', 'Todas') },
    { value: 'semanal', label: t('recurrences.frequency.weekly') },
    { value: 'quinzenal', label: t('recurrences.frequency.biweekly') },
    { value: 'mensal', label: t('recurrences.frequency.monthly') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm">{t('common.filters')}</h4>
        {temFiltrosAtivos && (
          <Button variant="ghost" size="sm" onClick={limpar}>
            <X className="mr-1 h-3 w-3" />
            {t('common.clear', 'Limpar')}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('common.status')}
        </Label>
        <div className="flex flex-wrap gap-2">
          {statusOpts.map((opt) => (
            <Button
              key={opt.value}
              variant={filtros.status === opt.value ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() => onChange({ ...filtros, status: opt.value })}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('recurrences.edit.frequencyLabel')}
        </Label>
        <div className="flex flex-wrap gap-2">
          {freqOpts.map((opt) => {
            const active =
              opt.value === 'todas'
                ? filtros.frequencia === undefined
                : filtros.frequencia === opt.value;
            return (
              <Button
                key={opt.value}
                variant={active ? 'default' : 'outline'}
                size="sm"
                className="rounded-full"
                onClick={() =>
                  onChange({
                    ...filtros,
                    frequencia:
                      opt.value === 'todas' ? undefined : (opt.value as FrequenciaRecorrencia),
                  })
                }
              >
                {opt.label}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('orders.form.provider')}
        </Label>
        <Select
          value={filtros.prestadorId ?? '__all__'}
          onValueChange={(v) =>
            onChange({
              ...filtros,
              prestadorId: v === '__all__' ? undefined : v,
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('common.all', 'Todos')}</SelectItem>
            <SelectItem value="__none__">{t('recurrences.card.pool')}</SelectItem>
            {prestadores?.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.profile?.nome || t('common.noName')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
